import styled from "@emotion/native";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, TouchableOpacity, View } from "react-native";
import { useReactiveVar } from "react-reactive-var";
import {
  chatSidebarCollapsed,
  isConnectedToCloud,
  isElectronRunning,
  lastPathInWorkspace,
  mrcSidebarCollapsed,
  pinnedSideMenu,
  showSideMenu,
  userType,
} from "../../../App";
import {
  forcedElectronChrome,
  forcedElectronChromePlatform,
  isElectronChrome,
} from "../../Utils/forceElectronChrome";
import Files from "../../API/Files";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { generatePath } from "react-router";
import { Link, matchPath, useLocation, useHistory } from "../../Router";
import { routes } from "../../Router/routes";
import { appDimensions, isMobileScreen } from "../../Screens/MainViewContainer";
import { MenuItem, MenuRow, ExpandableMenuItem } from "./shared";
import { SideBar } from "./SideBar";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import usePath from "../../Hooks/usePath";
import {
  useSettingsMenuItems,
  useAdminMenuItems,
  useProfileMenuItems,
} from "../../Hooks/useSectionMenuItems";
import Constants from "../../Constants";
import { useMemo } from "react";
import ToothFairyLoadingPlaceholder from "../ToothFairyComponents/ToothFairyLoadingPlaceholder";

const Content = styled(View)`
  height: 100%;
`;

const ConditionalLink = ({ children, to, condition, style, onClick }) => {
  return condition && to ? (
    <Link to={to} style={style} onClick={onClick}>
      {children}
    </Link>
  ) : (
    <AppText isClickable={true} isHoverable={false} onPress={() => {}}>
      {children}
    </AppText>
  );
};

//Using styled with Link renders an error
export const MenuLink = ({ children, to }) => {
  const { pathname } = useLocation();
  const { isInWorkspace } = usePath();
  const { state } = AppStateManager.useAppStateManager();
  const closeMenu = useCallback(() => {
    if (isMobileScreen()) showSideMenu(false);
    if ((to === routes.workspaces || to === routes.profile) && isInWorkspace) {
      lastPathInWorkspace({
        workspaceid: state?.activeWorkspace?.id,
        path: pathname,
      });
    }
  }, [pathname, isInWorkspace, state?.activeWorkspace?.id, to]);
  const _menuLinkView = useMemo(() => {
    if (Platform.OS === "web") {
      return (
        <ConditionalLink
          condition={!state?.changesNotSaved}
          style={{ textDecoration: "none" }}
          to={to}
          onClick={closeMenu}
        >
          {children}
        </ConditionalLink>
      );
    } else {
      return (
        <Link
          component={TouchableOpacity}
          style={{ textDecoration: "none" }}
          to={to}
          onPress={closeMenu}
          onClick={closeMenu}
        >
          {children}
        </Link>
      );
    }
  }, [children, to, state?.changesNotSaved, closeMenu]);

  return _menuLinkView;
};

const AgentsMenuItem = ({ icon, label, to, current, accessibilityLabel }) => {
  const history = useHistory();
  const location = useLocation();

  const isOnChatsRoute = useMemo(() => {
    return !!matchPath(location?.pathname, {
      path: `/workspaces/:workspaceId/chats`,
      exact: false,
    });
  }, [location?.pathname]);

  const handlePress = useCallback(() => {
    if (isMobileScreen()) showSideMenu(false);
    if (!isOnChatsRoute) {
      history.push(to);
    }
  }, [to, history, isOnChatsRoute]);

  return (
    <MenuRow
      icon={icon}
      label={label}
      current={current}
      accessibilityLabel={accessibilityLabel}
      onPress={handlePress}
    />
  );
};

const KnowledgeHubMenuItem = ({
  icon,
  label,
  to,
  current,
  accessibilityLabel,
}) => {
  const location = useLocation();
  const history = useHistory();
  const isSidebarCollapsed = useReactiveVar(mrcSidebarCollapsed);

  const isOnMrcRoute = useMemo(() => {
    return (
      matchPath(location?.pathname, {
        path: `/workspaces/:workspaceId/mrc_contents`,
        exact: false,
      })?.isExact ||
      matchPath(location?.pathname, {
        path: `/workspaces/:workspaceId/mrc_contents/:documentId`,
        exact: true,
      })?.isExact
    );
  }, [location?.pathname]);

  const handlePress = useCallback(() => {
    if (isMobileScreen()) showSideMenu(false);
    if (isOnMrcRoute) {
      mrcSidebarCollapsed(!isSidebarCollapsed);
    } else {
      history.push(to);
    }
  }, [isOnMrcRoute, isSidebarCollapsed, to, history]);

  return (
    <MenuRow
      icon={icon}
      label={label}
      current={current}
      accessibilityLabel={accessibilityLabel}
      onPress={handlePress}
    />
  );
};

const SideMenu = ({ isInWorkspace, handleSignOut }) => {
  const { t } = useTranslation();
  const { height, width } = useReactiveVar(appDimensions);
  const { currentTheme, mode } = AppTheme.useTheme();
  const showMenu = useReactiveVar(showSideMenu);
  const { state } = AppStateManager.useAppStateManager();
  const {
    isWorkspaceEnabled,
    isUserAdmin,
    isUserAIEngineer,
    isUserContentAdmin,
    isUserRoot,
    isToothFairyStaff,
    getPlanConfig,
    getActiveWorkspace,
  } = useWorkspaces();
  const isPinned = useReactiveVar(pinnedSideMenu);
  const isMobile = width < Constants.screenResponsiveWidth;
  const location = useLocation();
  const history = useHistory();

  const settingsBuilderRoutes = {
    ROLES_SETTINGS: routes.agents,
    GENERATOR_SETTINGS: routes.prompts,
    FUNCTIONS: routes.tools,
    EXEC_ENVS: routes.hooks,
    AUTHORISATIONS: routes.authorisations,
    JOBS: routes.jobs,
    TRIGGERS: routes.triggers,
    SITES: routes.sites,
    DB_CONNECTIONS: routes.connections,
    HOSTINGS: routes.hostings,
    CHANNELS: routes.channels,
    BENCHMARKS: routes.benchmarks,
  };

  // Platform detection for Electron (real runtime or scoped browser override)
  const forcedChrome = useReactiveVar(forcedElectronChrome);
  const forcedPlatform = useReactiveVar(forcedElectronChromePlatform);
  const [realPlatform, setRealPlatform] = useState(null);
  useEffect(() => {
    if (isElectronRunning() && window.electronAPI && !forcedChrome) {
      window.electronAPI.getPlatform().then(setRealPlatform);
    }
  }, [forcedChrome]);
  const chrome = isElectronChrome();
  const platform = forcedChrome ? forcedPlatform : realPlatform;

  // Workspace logo state
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState(null);
  const [workspaceLogoIsWide, setWorkspaceLogoIsWide] = useState(false);

  // Load workspace logo when inside a workspace
  useEffect(() => {
    const loadWorkspaceLogo = async () => {
      if (isInWorkspace) {
        const workspace = getActiveWorkspace();
        if (workspace?.id && workspace?.logo) {
          try {
            const _url = await Files.downloadUrlGeneration({
              filename: `imported-image/${workspace.id}/${workspace.logo}`,
              context: "pdf",
              workspaceid: workspace.id,
            });
            const logoUrl = _url?.data?.url || _url?.url;
            if (logoUrl) {
              setWorkspaceLogoUrl(logoUrl);
              // Probe aspect ratio: wide wordmark logos get the flat full-width
              // treatment, square-ish legacy icons keep the rounded-icon look.
              const probe = new Image();
              probe.onload = () =>
                setWorkspaceLogoIsWide(
                  probe.naturalWidth > probe.naturalHeight * 1.5
                );
              probe.onerror = () => setWorkspaceLogoIsWide(false);
              probe.src = logoUrl;
            } else {
              setWorkspaceLogoUrl(null);
              setWorkspaceLogoIsWide(false);
            }
          } catch (error) {
            setWorkspaceLogoUrl(null);
            setWorkspaceLogoIsWide(false);
          }
        } else {
          setWorkspaceLogoUrl(null);
          setWorkspaceLogoIsWide(false);
        }
      } else {
        setWorkspaceLogoUrl(null);
        setWorkspaceLogoIsWide(false);
      }
    };
    loadWorkspaceLogo();
  }, [isInWorkspace, getActiveWorkspace()?.id, getActiveWorkspace()?.logo]);

  const _appLogo = useMemo(
    () => (
      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
          paddingTop: 8,
          paddingLeft: 10,
          width: "100%",
          marginBottom: workspaceLogoUrl ? 4 : 7,
        }}
      >
        {chrome && platform === "darwin" ? (
          <View
            style={{
              width: 40,
              height: 25,
              marginBottom: 4,
            }}
          ></View>
        ) : workspaceLogoUrl ? (
          <img
            src={workspaceLogoUrl}
            style={
              workspaceLogoIsWide
                ? {
                    height: 22,
                    maxWidth: 180,
                    objectFit: "contain",
                    borderRadius: 4,
                    cursor: "pointer",
                    marginBottom: 0,
                  }
                : {
                    width: "auto",
                    height: 19,
                    borderRadius: 12,
                    cursor: "pointer",
                    marginBottom: 0,
                  }
            }
            onClick={() => {
              history.push(routes.workspaces);
            }}
            alt="Workspace logo"
          />
        ) : (
          <img
            src={require("../../assets/logoTitle.png")}
            style={{
              height: 19,
              maxWidth: 180,
              objectFit: "contain",
              cursor: "pointer",
            }}
            onClick={() => {
              history.push(routes.workspaces);
            }}
            alt="ToothFairyAI"
          />
        )}
      </View>
    ),
    [chrome, showSideMenu, platform, workspaceLogoUrl, workspaceLogoIsWide]
  );
  const multiPathMatch = useCallback(
    (paths) => {
      let matchFound = false;
      paths.forEach((element) => {
        if (
          matchPath(location?.pathname, {
            path: element,
            exact: true,
          })?.isExact
        )
          matchFound = true;
      });
      return matchFound;
    },
    [location?.pathname]
  );
  // Synchronously derive whether the CURRENT URL is a workspace route.
  // Mirrors usePath's isInWorkspace decision (exact match against every
  // route) but computed during render, so it is correct in the same render
  // the route changes. usePath flips isInWorkspace in a post-paint
  // useEffect, which leaves a one-frame window right after navigating into a
  // workspace where activeWorkspaceID is already set but isInWorkspace is
  // still false — the outside-workspace Back button flashes in that frame.
  // Gating the Back button on this removes the flash.
  const routeIsInsideWorkspace = useMemo(() => {
    if (!location?.pathname) return false;
    for (const [, routePath] of Object.entries(routes)) {
      const match = matchPath(location.pathname, {
        path: routePath,
        exact: true,
      });
      if (match?.isExact) {
        return !(
          routePath === routes.workspaces ||
          routePath === routes.profile ||
          routePath === routes.error ||
          routePath === routes.accessDenied
        );
      }
    }
    return false;
  }, [location?.pathname]);
  const {
    section,
    path,
    lastNLPdocumentOpened,
    lastRCdocumentOpened,
    lastMRCdocumentOpened,
    lastGNdocumentOpened,
    lastDashboardTabOpened,
    lastDocumentsTabOpened,
    lastTrainingTabOpened,
    lastBatchTabOpened,
    lastSettingsTabOpened,
    lastAdminTabOpened,
    lastArenaTabOpened,
    lastProfileTabOpened,
    lastChatOpened,
  } = usePath();
  const logoutButton = (
    <MenuRow
      icon={AppAssets?.icons?.signOut}
      label={t("sidemenu.logout")}
      onPress={handleSignOut}
      accessibilityLabel="LogoutButton"
      wrapperStyle={{
        marginLeft: 0,
      }}
    />
  );
  const _outsideWorkspaceMenuItems = useMemo(() => {
    return (
      <View
        style={{
          marginBottom: 10,
        }}
      >
        <MenuItem
          to={routes.workspaces}
          label={t("sidemenu.workspaces")}
          icon={AppAssets.icons.workspace}
          accessibilityLabel="WorkspacesRoute"
        />
        <MenuItem
          to={_profileTo}
          current={multiPathMatch([`/profile`, `/profile/:sectionId`])}
          label={t("sidemenu.profile")}
          icon={AppAssets.icons.account}
          accessibilityLabel="ProfileRoute"
        />
        {state?.activeWorkspaceID &&
          !isInWorkspace &&
          isWorkspaceEnabled &&
          goBackButton}
      </View>
    );
  }, [
    isInWorkspace,
    isWorkspaceEnabled,
    state?.activeWorkspaceID,
    multiPathMatch,
    _profileTo,
    goBackButton,
    isUserAdmin(),
    t,
  ]);
  const goBackButton = (
    <MenuRow
      icon={AppAssets?.icons?.back_icon}
      label={t("sidemenu.back")}
      accessibilityLabel="GoBackButton"
      onPress={() => {
        if (lastPathInWorkspace()?.path)
          history?.push(lastPathInWorkspace()?.path);
      }}
    />
  );
  // The "last opened" paths are recorded unqualified, so after switching
  // workspaces they still carry the PREVIOUS workspace id and would silently
  // navigate back into it (e.g. the starred one). Only trust them while they
  // match the currently active workspace.
  const lastTabPathForWorkspace = (lastPath, fallback) => {
    const id = state.activeWorkspaceID;
    return lastPath && lastPath.startsWith(`/workspaces/${id}/`)
      ? lastPath
      : fallback;
  };
  const _dashboardTo = lastTabPathForWorkspace(
    lastDashboardTabOpened,
    `/workspaces/${state.activeWorkspaceID}/dashboard/model`,
  );
  const _nlpTo = `/workspaces/${state.activeWorkspaceID}/nlp`;
  const _rcTo =
    section === "RC_TRAINER"
      ? path.url
      : lastTabPathForWorkspace(
          lastRCdocumentOpened,
          `/workspaces/${state.activeWorkspaceID}/answerer`,
        );
  const _mrcTo =
    section === "MRC_DOCUMENTS"
      ? path.url
      : lastTabPathForWorkspace(
          lastMRCdocumentOpened,
          `/workspaces/${state.activeWorkspaceID}/mrc_contents`,
        );
  const _gnTo =
    section === "GN_DOCUMENTS"
      ? path.url
      : lastTabPathForWorkspace(
          lastGNdocumentOpened,
          `/workspaces/${state.activeWorkspaceID}/generator`,
        );

  const _homeTo = `/workspaces/${state.activeWorkspaceID}/home`;
  const _chatsTo =
    section === "CHATS"
      ? path.url
      : lastTabPathForWorkspace(
          lastChatOpened,
          `/workspaces/${state.activeWorkspaceID}/chats`,
        );
  const _documentsTo = lastTabPathForWorkspace(
    lastDocumentsTabOpened,
    `/workspaces/${state.activeWorkspaceID}/documents/cn`,
  );
  const _trainingTo = lastTabPathForWorkspace(
    lastTrainingTabOpened,
    `/workspaces/${state.activeWorkspaceID}/training/cn`,
  );
  const _batchTo = lastTabPathForWorkspace(
    lastBatchTabOpened,
    `/workspaces/${state.activeWorkspaceID}/batch/gn`,
  );
  const _settingsTo =
    section === "SETTINGS"
      ? path.url
      : lastTabPathForWorkspace(
          lastSettingsTabOpened,
          `/workspaces/${state.activeWorkspaceID}/settings/${
            isUserAIEngineer() ? "roles_settings" : "topic"
          }`,
        );
  const _adminTo =
    section === "ADMIN"
      ? path.url
      : lastTabPathForWorkspace(
          lastAdminTabOpened,
          `/workspaces/${state.activeWorkspaceID}/admin/workspace_settings`,
        );
  const _arenaTo =
    section === "ARENA"
      ? path.url
      : lastTabPathForWorkspace(
          lastArenaTabOpened,
          `/workspaces/${state.activeWorkspaceID}/arena`,
        );
  const _profileTo =
    section === "PROFILE_SETTINGS"
      ? path.url
      : lastProfileTabOpened
      ? lastProfileTabOpened
      : `/profile/profile_settings`;
  const { user } = AppUser.useUser();
  const { isBatchEnabled } = useWorkspaces();

  const settingsMenuItems = useSettingsMenuItems();
  const adminMenuItems = useAdminMenuItems();
  const profileMenuItems = useProfileMenuItems();

  const profileSubItems = useMemo(() => {
    return (profileMenuItems || []).map((item) => ({
      uid: item.uid,
      label: item.label,
      icon: item.icon,
      to: `/profile/${item.uid.toLowerCase()}`,
    }));
  }, [profileMenuItems]);

  const isOnProfileRoute = !!matchPath(location?.pathname, {
    path: `/profile`,
    exact: false,
  });

  const settingsSubItems = useMemo(() => {
    const seen = new Set();
    return (settingsMenuItems || [])
      .filter((item) => {
        if (seen.has(item.uid)) return false;
        seen.add(item.uid);
        return true;
      })
      .map((item) => {
        const builderRoute = settingsBuilderRoutes[item.uid];
        const legacyTo = `/workspaces/${
          state.activeWorkspaceID
        }/settings/${item.uid.toLowerCase()}`;
        const builderTo = builderRoute
          ? generatePath(builderRoute, {
              workspaceId: state.activeWorkspaceID,
            })
          : null;
        return {
          uid: item.uid,
          label: item.label,
          icon: item.icon,
          to: builderTo || legacyTo,
          // Highlight when either the builder route or the legacy settings
          // route is active (deep links / saved URLs may still land on the
          // legacy /settings/<uid> paths).
          matchPatterns: builderTo ? [builderTo, legacyTo] : [legacyTo],
        };
      });
  }, [settingsMenuItems, state.activeWorkspaceID]);

  const adminSubItems = useMemo(() => {
    return (adminMenuItems || []).map((item) => ({
      uid: item.uid,
      label: item.label,
      icon: item.icon,
      to: `/workspaces/${
        state.activeWorkspaceID
      }/admin/${item.uid.toLowerCase()}`,
    }));
  }, [adminMenuItems, state.activeWorkspaceID]);

  const isOnSettingsRoute =
    Object.values(settingsBuilderRoutes).some(
      (pattern) =>
        !!matchPath(location?.pathname, { path: pattern, exact: false })
    ) ||
    !!matchPath(location?.pathname, {
      path: `/workspaces/:workspaceId/settings`,
      exact: false,
    });
  const isOnAdminRoute = !!matchPath(location?.pathname, {
    path: `/workspaces/:workspaceId/admin`,
    exact: false,
  });

  const _inWorkspaceMenuItems = useMemo(() => {
    return (
      <View
        style={{
          flexDirection: "column",
          justifyContent: "space-between",
          paddingTop: 10,
          // marginLeft: 5,
          minWidth: 200,
          marginTop: 3,
          marginRight: 6,
        }}
      >
        <MenuItem
          current={multiPathMatch([
            `/workspaces/:workspaceId/home`,
            `/workspaces/:workspaceId/home/:chatId`,
          ])}
          to={_homeTo}
          label={t("sidemenu.home")}
          accessibilityLabel="HomeRoute"
          icon={AppAssets.icons.home}
        />
        <AgentsMenuItem
          current={multiPathMatch([
            `/workspaces/:workspaceId/chats`,
            `/workspaces/:workspaceId/chats/:chatId`,
          ])}
          to={_chatsTo}
          label={t("sidemenu.agents")}
          accessibilityLabel="ChatRoute"
          icon={AppAssets.icons.taskAgent}
        />
        {getPlanConfig()?.isContentCreatorEnabled && isConnectedToCloud() && (
          <MenuItem
            current={multiPathMatch([
              `/workspaces/:workspaceId/generator`,
              `/workspaces/:workspaceId/generator/:documentId`,
            ])}
            to={_gnTo}
            label={t("sidemenu.contentGeneration")}
            accessibilityLabel="GNRoute"
            icon={AppAssets.icons.writer}
          />
        )}
        {getPlanConfig()?.isBusinessAnalystEnabled && isConnectedToCloud() && (
          <MenuItem
            current={multiPathMatch([
              `/workspaces/:workspaceId/answerer`,
              `/workspaces/:workspaceId/answerer/:documentId`,
            ])}
            to={_rcTo}
            label={t("sidemenu.readingComprehension")}
            accessibilityLabel="RCRoute"
            icon={AppAssets.icons.ba}
          />
        )}

        {isConnectedToCloud() &&
          getPlanConfig()?.isSentimentReviewerEnabled && (
            <MenuItem
              current={false}
              to={_nlpTo}
              label={t("sidemenu.sentimentReviewer")}
              icon={AppAssets.icons.trainingLog}
              accessibilityLabel="NLPRoute"
            />
          )}

        {(isUserContentAdmin() || isUserAIEngineer()) &&
          isConnectedToCloud() && (
            <KnowledgeHubMenuItem
              current={multiPathMatch([
                `/workspaces/:workspaceId/mrc_contents`,
                `/workspaces/:workspaceId/mrc_contents/:documentId`,
              ])}
              to={_mrcTo}
              label={t("sidemenu.knowledgeHub")}
              accessibilityLabel="MRCRoute"
              icon={AppAssets.icons.notes}
            />
          )}

        <>
          {(isUserContentAdmin() || isUserAIEngineer()) && (
            <ExpandableMenuItem
              icon={AppAssets.icons.settings}
              label={t("sidemenu.settings")}
              items={settingsSubItems}
              isActive={isOnSettingsRoute}
              accessibilityLabel="SettingsRoute"
            />
          )}

          {isUserAIEngineer() && isConnectedToCloud() && (
            <ExpandableMenuItem
              icon={AppAssets.icons.advanced}
              label={t("sidemenu.advanced")}
              items={[
                isUserAdmin() && {
                  uid: "DASHBOARD",
                  label: t("sidemenu.dashboard"),
                  icon: AppAssets.icons.dashboard,
                  to: _dashboardTo,
                },
                isUserAIEngineer() && {
                  uid: "DOCUMENTS",
                  label: t("sidemenu.documents"),
                  icon: AppAssets.icons.trainAI,
                  to: _documentsTo,
                },
                isUserAIEngineer() && {
                  uid: "TRAINING",
                  label: t("sidemenu.fineTuning"),
                  icon: AppAssets.icons.slidersH,
                  to: _trainingTo,
                },
                isUserAIEngineer() &&
                  isBatchEnabled() && {
                    uid: "BATCH",
                    label: t("sidemenu.benchmarks"),
                    icon: AppAssets.icons.benchmark,
                    to: _batchTo,
                  },
                isUserAdmin() && {
                  uid: "ARENA",
                  label: t("sidemenu.arena"),
                  icon: AppAssets.icons.arena,
                  to: _arenaTo,
                },
              ].filter(Boolean)}
              isActive={multiPathMatch([
                `/workspaces/:workspaceId/dashboard/:sectionId`,
                `/workspaces/:workspaceId/documents`,
                `/workspaces/:workspaceId/documents/:sectionId`,
                `/workspaces/:workspaceId/documents/:sectionId/:documentId`,
              ])}
              accessibilityLabel="AdvancedRoute"
            />
          )}
          {(isUserAdmin() || isToothFairyStaff()) && (
            <ExpandableMenuItem
              icon={AppAssets.icons.admin}
              label={t("sidemenu.admin")}
              items={adminSubItems}
              isActive={isOnAdminRoute}
              accessibilityLabel="AdminRoute"
            />
          )}
        </>
      </View>
    );
  }, [
    getActiveWorkspace()?.id,
    isBatchEnabled(),
    user?.id,
    _homeTo,
    _adminTo,
    _arenaTo,
    _dashboardTo,
    _documentsTo,
    _nlpTo,
    _rcTo,
    _mrcTo,
    _gnTo,
    _settingsTo,
    _trainingTo,
    settingsSubItems,
    adminSubItems,
    isOnSettingsRoute,
    isOnAdminRoute,
    location?.pathname,
    userType(),
    isConnectedToCloud(),
    t,
  ]);
  return (
    <SideBar
      showMenu={showMenu}
      width={width}
      height={height}
      isPinned={isPinned}
      currentTheme={currentTheme}
    >
      <Content height={height} width={width}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            backgroundColor: currentTheme?.theme?.backgroundColorAppBorders,
          }}
        >
          {_appLogo}
        </View>
        <View
          style={{
            flexDirection: "column",
            justifyContent: "space-between",
            overflowY: "auto",
            overflowX: "hidden",
            flex: 1,
            backgroundColor: currentTheme?.theme?.backgroundColorAppBorders,
            borderTopColor: currentTheme?.theme?.app_outline_color,
            // borderTopWidth: 1,
            borderRightWidth: 1,
            borderRightColor: currentTheme?.theme?.app_outline_color,
          }}
        >
          {!userType() && isInWorkspace ? (
            <ToothFairyLoadingPlaceholder caption="" brandedAnimation />
          ) : (
            isInWorkspace && userType() && _inWorkspaceMenuItems
          )}
          <View
            style={{
              marginBottom: 10,
              justifyContent: "space-between",
            }}
          >
            <View
              style={{
                minWidth: 200,
                marginTop: 3,
                paddingTop: 10,
                // marginLeft: 5,
                marginRight: 6,
              }}
            >
              <MenuItem
                to={routes.workspaces}
                label={t("sidemenu.workspaces")}
                icon={AppAssets.icons.workspace}
                accessibilityLabel="WorkspacesRoute"
              />
              <ExpandableMenuItem
                icon={AppAssets.icons.account}
                label={t("sidemenu.profile")}
                items={profileSubItems}
                isActive={isOnProfileRoute}
                accessibilityLabel="ProfileRoute"
              />
              {state?.activeWorkspaceID &&
                !isInWorkspace &&
                isWorkspaceEnabled &&
                !routeIsInsideWorkspace &&
                !multiPathMatch([routes.accessDenied]) &&
                lastPathInWorkspace() &&
                goBackButton}
            </View>
            {isInWorkspace && logoutButton}
          </View>
          {!isInWorkspace && logoutButton}
        </View>
      </Content>
    </SideBar>
  );
};

export default SideMenu;
